/**
 * CombatHUD — health, abilities, alive count, match clock, live pot.
 *
 * Every number here is a mirror of server state. The HUD has no path to compute
 * a health value, a cooldown, or a pot — it reads the snapshot, the Schema and
 * the room's `result` message. Cooldowns in particular are ABSOLUTE SERVER
 * TICKS on the wire (`PlayerSnapshot.cooldowns`), so the remaining time is
 * `(readyTick - currentServerTick) / TICK_HZ` — never a local countdown timer,
 * which would drift and eventually show "ready" on a button the server refuses.
 *
 * The pot is a wei string, formatted only here, at the UI boundary, and never
 * parsed back into a number for anything.
 */

import { CHARACTERS, TICK_HZ } from '@chaos/protocol';
import { DASH_COOLDOWN_MS } from '@chaos/sim';
import { abilityCopy } from './abilityText.js';
import { escapeHtml } from './html.js';
import { isTouch } from '../config.js';
import { setCooldown as setTouchCooldown, setUltCharged } from '../input/MobileControls.js';
import { press, release } from '../input/InputManager.js';
import { playUltReadySound } from '../audio/SoundManager.js';

const SLOTS = [
  { slot: 'basic', action: 'basic', key: 'LMB / C', glyph: 'ATK' },
  { slot: 'skill1', action: 'skill1', key: 'Q', glyph: '1' },
  { slot: 'skill2', action: 'skill2', key: 'E', glyph: '2' },
  { slot: 'ultimate', action: 'ultimate', key: 'R', glyph: 'ULT' },
];

const DASH_SLOT = { slot: 'dash', action: 'dash', key: 'F / RMB', glyph: 'DSH' };


export class CombatHUD {
  constructor() {
    this.root = document.getElementById('combat-hud');
    this.characterId = 'volt';
    this._ultWasReady = false;
    /** The bar slot currently held by the mouse, so a release outside it lands. */
    this._pointerHeld = null;
    this._build();
  }

  _build() {
    this.root.innerHTML = `
      <div class="hud-top">
        <div class="hud-chip alive"><div class="label">Alive</div><div class="value" data-alive>8</div></div>
        <div class="hud-chip clock"><div class="label">Time</div><div class="value" data-clock>4:00</div></div>
        <div class="hud-chip pot"><div class="label">Pot</div><div class="value" data-pot>—</div></div>
      </div>
      <div class="ability-row" data-abilities></div>
      <div class="ability-tip" data-tip hidden></div>
    `;

    this.aliveEl = this.root.querySelector('[data-alive]');
    this.clockEl = this.root.querySelector('[data-clock]');
    this.clockChip = this.root.querySelector('.hud-chip.clock');
    this.potEl = this.root.querySelector('[data-pot]');

    this.tipEl = this.root.querySelector('[data-tip]');

    const row = this.root.querySelector('[data-abilities]');
    this.abilityEls = new Map();
    for (const spec of [DASH_SLOT, ...SLOTS]) {
      const el = document.createElement('button');
      el.type = 'button';
      // The ultimate gets its own class: bigger, and the only pink control on
      // screen. Size and hue both say "this is the one you were saving".
      el.className = spec.slot === 'ultimate' ? 'ability ult-slot' : 'ability';
      const charge = spec.slot === 'ultimate' ? '<span class="charge"></span>' : '';
      el.innerHTML = `${charge}<span class="glyph">${spec.glyph}</span><span class="cd"></span><span class="cd-text"></span><span class="key">${spec.key}</span>`;
      this._wireAbilityButton(el, spec.action);
      this._wireTooltip(el, spec);
      row.appendChild(el);
      this.abilityEls.set(spec.action, el);
    }

    // A press that starts on a button and ends anywhere else must still release.
    window.addEventListener('mouseup', () => this._releasePointerHeld());
    window.addEventListener('blur', () => this._releasePointerHeld());
  }

  /**
   * Hover, or keyboard focus, explains the button.
   *
   * ── Why a tooltip at all ────────────────────────────────────────────────
   *
   * The bar showed five glyphs and a key hint. "1" and "2" say nothing about
   * what they do, how far they reach, whether they can be reacted to, or why
   * you would press one rather than the other — and this is a wagered match, so
   * a player guessing at their own kit is a player losing money to the UI.
   *
   * Everything in it is read from `CHARACTERS` at hover time, which is the same
   * table the SERVER resolves hits from. A tooltip that promises 9 damage in a
   * 2.6m cone while the sim deals 8 in a 3m one is worse than no tooltip,
   * because the player then plans around it.
   *
   * FOCUS AS WELL AS HOVER: these are real `<button>`s and tabbing to one must
   * explain it too, or the bar is unreadable to anyone not using a mouse.
   *
   * Touch gets nothing here on purpose. On a phone these buttons are drag-to-
   * aim controls — press, drag, release to cast — so a long-press tooltip would
   * fight the gesture that fires the ability. Mobile needs this on the character
   * screen instead, where there is room and nothing to interrupt.
   */
  _wireTooltip(el, spec) {
    const show = () => this._showTip(el, spec);
    const hide = () => this._hideTip();
    el.addEventListener('mouseenter', show);
    el.addEventListener('mouseleave', hide);
    el.addEventListener('focus', show);
    el.addEventListener('blur', hide);
  }

  _showTip(el, spec) {
    if (isTouch) return;
    const copy = abilityCopy(this.characterId, spec.slot);
    if (!copy) return;

    this.tipEl.innerHTML =
      `<div class="tip-head"><span class="tip-name">${escapeHtml(copy.name)}</span>` +
      `<span class="tip-key">${escapeHtml(spec.key)}</span></div>` +
      `<div class="tip-stats">${copy.stats.map((t) => `<span>${escapeHtml(t)}</span>`).join('')}</div>` +
      `<div class="tip-shape">${escapeHtml(copy.shape)}</div>` +
      `<p class="tip-body">${escapeHtml(copy.description)}</p>`;
    this.tipEl.hidden = false;

    // Centred over its button and clamped to the viewport, so the ultimate at
    // the end of the row does not push its own tooltip off screen.
    const r = el.getBoundingClientRect();
    const w = this.tipEl.offsetWidth;
    const margin = 10;
    const left = Math.min(
      Math.max(margin, r.left + r.width / 2 - w / 2),
      window.innerWidth - w - margin
    );
    this.tipEl.style.left = `${left}px`;
    this.tipEl.style.bottom = `${window.innerHeight - r.top + 12}px`;
  }

  _hideTip() {
    if (this.tipEl) this.tipEl.hidden = true;
  }

  /**
   * Makes one bar slot a real control.
   *
   * The bar was `pointer-events: none` divs styled as raised buttons: it looked
   * clickable and did nothing, which on desktop is the only visible affordance
   * for abilities a trackpad user cannot otherwise reach. It drives `press` and
   * `release` from InputManager — the same two functions the keyboard and the
   * touch cluster call — so a click produces a bit-identical InputFrame and the
   * server cannot tell which device sent it.
   *
   * Locked slots still dispatch. The keyboard does not gate Q on a cooldown and
   * neither does the touch cluster; the sim refuses the cast, and a bar that
   * silently swallowed the press would be a third input scheme.
   */
  _wireAbilityButton(el, action) {
    el.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      // InputManager fires the basic attack from a document-level `mousedown`,
      // so without this, clicking DSH would also swing. `mouseup` is left to
      // propagate: swallowing it would strand a press that began on the canvas.
      e.stopPropagation();
      // Also suppresses focus, so clicking a slot never takes the keyboard away
      // from the canvas. Tab still reaches it.
      e.preventDefault();
      this._pointerHeld = action;
      press(action);
      el.classList.add('pressed');
    });

    el.addEventListener('mouseup', () => this._releasePointerHeld());
    el.addEventListener('mouseleave', () => this._releasePointerHeld());

    el.addEventListener('keydown', (e) => {
      // Space is jump everywhere, and a focused button must not steal it into a
      // cast as well — cancelling it here stops the browser's own activation.
      // Enter is the keyboard activation.
      if (e.key === ' ') {
        e.preventDefault();
        return;
      }
      if (e.key !== 'Enter' || e.repeat) return;
      e.preventDefault();
      press(action);
      el.classList.add('pressed');
    });

    el.addEventListener('keyup', (e) => {
      if (e.key !== 'Enter') return;
      release(action);
      el.classList.remove('pressed');
    });

    el.addEventListener('blur', () => {
      release(action);
      el.classList.remove('pressed');
    });
  }

  _releasePointerHeld() {
    const action = this._pointerHeld;
    if (!action) return;
    this._pointerHeld = null;
    release(action);
    this.abilityEls.get(action)?.classList.remove('pressed');
  }

  setCharacter(characterId) {
    this.characterId = characterId;
    const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
    // Still published, because the ability buttons and the ult ring tint from
    // it — the same number that colours this character's telegraphs and VFX out
    // in the world, rather than a second palette that agrees today.
    this.root.style.setProperty('--self-color', `#${def.colorHex.toString(16).padStart(6, '0')}`);
    // A tooltip open across a character swap would keep describing the old kit.
    this._hideTip();
    for (const spec of SLOTS) {
      const el = this.abilityEls.get(spec.action);
      const ability = def.abilities[spec.slot];
      if (el && ability) el.title = `${ability.name} — ${ability.description}`;
    }
  }

  setPot(potWei) {
    this.potEl.textContent = formatEth(potWei);
  }

  /**
   * @param {object} mine PlayerSnapshot for the local player
   * @param {number} serverTick the snapshot's tick — cooldowns are relative to it
   */
  update(mine, serverTick, match) {
    const def = CHARACTERS[mine.characterId] ?? CHARACTERS.volt;

    // Charge fills the ring around the ULT button rather than a bar in the
    // corner — the mockups draw it that way and so does Brawl Stars, for the
    // same reason: the meter belongs on the control it unlocks, where your
    // thumb and your eye already are.
    const ultFrac = Math.max(0, Math.min(1, mine.ultCharge / 100));
    const ultReady = ultFrac >= 1;
    const ultEl = this.abilityEls.get('ultimate');
    if (ultEl) {
      ultEl.style.setProperty('--charge-deg', `${ultFrac * 360}deg`);
      ultEl.classList.toggle('charged', ultReady);
    }
    if (ultReady && !this._ultWasReady) playUltReadySound();
    this._ultWasReady = ultReady;

    this.aliveEl.textContent = String(match.aliveCount);
    this.clockEl.textContent = formatClock(match.timeRemainingMs);
    this.clockChip.classList.toggle('urgent', match.timeRemainingMs <= 30_000);

    // Dash lives outside the roster table — it is universal, and the sim files
    // its cooldown under the ability id "dash".
    this._updateAbility('dash', mine.cooldowns['dash'], serverTick, DASH_COOLDOWN_MS, true);
    for (const spec of SLOTS) {
      const ability = def.abilities[spec.slot];
      const total = spec.slot === 'ultimate' ? 0 : ability.cooldownMs;
      const usable = spec.slot === 'ultimate' ? ultReady : true;
      this._updateAbility(spec.action, mine.cooldowns[ability.id], serverTick, total, usable);
    }
    if (isTouch) setUltCharged(ultReady);
  }

  _updateAbility(action, readyTick, serverTick, totalMs, usable) {
    const el = this.abilityEls.get(action);
    // The ultimate has no cooldown; its gate is charge, and a ring would imply
    // a timer that does not exist.
    const remainingMs =
      totalMs > 0 && readyTick !== undefined
        ? Math.max(0, ((readyTick - serverTick) / TICK_HZ) * 1000)
        : 0;

    if (el) {
      const ready = usable && remainingMs <= 0;
      el.classList.toggle('ready', ready);
      el.classList.toggle('locked', !ready);
      el.style.setProperty('--cd-deg', remainingMs > 0 ? `${(remainingMs / totalMs) * 360}deg` : '0deg');
      const text = el.querySelector('.cd-text');
      if (text) text.textContent = remainingMs > 0 ? String(Math.ceil(remainingMs / 1000)) : '';
    }

    if (isTouch) setTouchCooldown(action, remainingMs, totalMs);
  }
}

function formatClock(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Wei -> a short ETH string, for display only.
 *
 * BigInt division throughout: `Number(wei) / 1e18` loses precision above 2^53
 * and is exactly the float-at-18-decimals mistake the money package exists to
 * avoid. Nothing reads this value back.
 */
function formatEth(weiString) {
  let wei;
  try {
    wei = BigInt(weiString ?? '0');
  } catch {
    return '—';
  }
  if (wei === 0n) return '—';

  const ONE = 10n ** 18n;
  const whole = wei / ONE;
  const frac = ((wei % ONE) * 10000n) / ONE;
  return `${whole}.${String(frac).padStart(4, '0')} Ξ`;
}

export { formatEth };

